import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { ShieldCheck, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { api } from '../utils/api';
import { useLocalizedNavigate } from '../i18n/useLocalizedNav';

const RESEND_COOLDOWN_SECONDS = 30;

const VerifyEmail: React.FC = () => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [otp, setOtp] = useState('');
  const [error, setError] = useState('');
  const [resendNotice, setResendNotice] = useState('');
  const [isResending, setIsResending] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const navigate = useLocalizedNavigate();
  const location = useLocation();
  const email = location.state?.email;

  // Tick the resend cooldown down to zero, then re-enable the button.
  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const handleResend = async () => {
    if (!email) {
      setError(t('verify_err_missing_email'));
      return;
    }
    setIsResending(true);
    setError('');
    setResendNotice('');
    try {
      const res = await api.post('/auth/resend-verification', { email });
      setResendNotice(res.message || t('verify_default_resend'));
      setCooldown(RESEND_COOLDOWN_SECONDS);
    } catch {
      setError(t('verify_err_resend'));
    } finally {
      setIsResending(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) {
      setError(t('verify_err_missing_email'));
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const res = await api.post('/auth/verify-email', { email, otp });
      if (res.error) {
        setError(res.error);
      } else {
        navigate('/login', { state: { message: t('verify_msg_success') } });
      }
    } catch (err) {
      setError(t('verify_err_verify'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto bg-white p-8 rounded-2xl border border-warm-200 shadow-sm space-y-6">
      <div className="text-center space-y-2">
        <div className="w-16 h-16 bg-brand-primary/20 rounded-full flex items-center justify-center mx-auto mb-4">
          <ShieldCheck className="text-brand-primary w-8 h-8" />
        </div>
        <h2 className="text-2xl">{t('verify_title')}</h2>
        <p className="text-warm-500">{t('verify_sent_to')} <br /><span className="font-bold text-warm-800">{email}</span></p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-1">
          <label className="text-sm font-bold text-warm-800 uppercase tracking-wider text-center block">{t('verify_code_label')}</label>
          <input 
            type="text" 
            placeholder="000000"
            maxLength={6}
            className="w-full text-center text-3xl tracking-[0.5em] py-4 bg-warm-100 border-none rounded-xl focus:ring-2 focus:ring-brand-primary outline-none font-mono"
            value={otp}
            onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
            required
          />
        </div>

        {error && <p className="text-brand-danger text-sm text-center font-bold">{error}</p>}

        <button 
          type="submit" 
          disabled={isLoading || otp.length !== 6}
          className="w-full py-3 bg-brand-primary text-brand-secondary font-heading font-bold rounded-xl shadow-lg hover:bg-brand-accent transition-all flex items-center justify-center disabled:opacity-50"
        >
          {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : t('verify_submit')}
        </button>
      </form>

      {resendNotice && (
        <p className="text-brand-success text-sm text-center font-bold">{resendNotice}</p>
      )}

      <p className="text-center text-warm-500 text-sm">
        {t('verify_no_code')}{' '}
        <button
          type="button"
          onClick={handleResend}
          disabled={isResending || cooldown > 0}
          className="text-brand-accent font-bold hover:underline disabled:opacity-50 disabled:no-underline disabled:cursor-not-allowed"
        >
          {isResending
            ? t('verify_sending')
            : cooldown > 0
              ? t('verify_resend_in', { count: cooldown })
              : t('verify_resend')}
        </button>
      </p>
    </div>
  );
};

export default VerifyEmail;
